import { Injectable } from "@angular/core";
import { LayoutService } from "./layout.service";
import { ProductReference } from "../models/layout/product-reference";
import { PlanogramProblemType } from "../models/planogram-problem-type";
import { BehaviorSubject, debounceTime } from "rxjs";
import { ImageService } from "./image.service";

@Injectable({
    providedIn: 'root'
})
export class PlanogramProblemsService {
    public problems: Map<PlanogramProblemType, ProductReference[]> = new Map();
    public hasProblems$ = new BehaviorSubject<boolean>(false);
    private hasRunInitialMissingImageCheck = false;

    constructor(
        private layoutService: LayoutService,
        private imageService: ImageService
    ) {
        // Problems are derived from planogramItems (dimensions, sales, flow),
        // which only changes on import/refresh/remove — not on every structural
        // mutation. Subscribing to layout$ caused 3 filter passes over 1000+
        // items per drag pickup/drop/undo step.
        this.layoutService.productsChanged$.subscribe(() => {
            this.refreshLayoutProblems();
        });

        // Track layout lifecycle (load/unload) for the one-shot missing-images check.
        this.layoutService.layout$.subscribe(layout => {
            if (layout) {
                if (!this.hasRunInitialMissingImageCheck) {
                    this.hasRunInitialMissingImageCheck = true;
                    this.refreshMissingImagesProblem();
                }
            } else {
                this.hasRunInitialMissingImageCheck = false;
            }
        });

        // Image changes affect missing image problems
        this.imageService.imageUpdates$
            .pipe(debounceTime(2000)) // ⏳ wait 2s after last image update
            .subscribe(() => this.refreshMissingImagesProblem());
    }

    /** Refresh all problems related to layout data only */
    private refreshLayoutProblems(): void {
        const layoutProducts = Array.from(this.layoutService.planogramItems.values());
        const problemsMap = new Map<PlanogramProblemType, ProductReference[]>(this.problems);

        problemsMap.set(PlanogramProblemType.MissingDimensions,
            layoutProducts.filter(p =>
                !p.dimensions ||
                !p.dimensions.width || !p.dimensions.height || !p.dimensions.depth
            )
        );

        problemsMap.set(PlanogramProblemType.NoSalesOrVolume,
            layoutProducts.filter(p =>
                !p.salesData ||
                ((!p.salesData.salesQty || p.salesData.salesQty <= 0) &&
                 (!p.salesData.sales || p.salesData.sales <= 0))
            )
        );

        problemsMap.set(PlanogramProblemType.FlowOrderZero,
            layoutProducts.filter(p => !p.categoryFlow || p.categoryFlow === 0)
        );

        this.problems = problemsMap;
        this.updateHasProblemsFlag();
    }

    /** Refresh the MissingImages problem category from backend */
    private refreshMissingImagesProblem(): void {
        const layoutProducts = Array.from(this.layoutService.planogramItems.values());
        const productTuples = layoutProducts.map(p => ({
            barcode: p.barcode,
            orientation: p.orientation
        }));

        this.imageService.detectMissingImages(productTuples).subscribe(missingList => {
            const missingProducts = layoutProducts.filter(lp =>
                missingList.some(m => m.barcode === lp.barcode && m.orientation === lp.orientation)
            );

            const problemsMap = new Map<PlanogramProblemType, ProductReference[]>(this.problems);
            problemsMap.set(PlanogramProblemType.MissingImages, missingProducts);

            this.problems = problemsMap;
            this.updateHasProblemsFlag();
        });
    }

    /** Updates the boolean flag indicating if there are any problems */
    private updateHasProblemsFlag(): void {
        const hasAny = Array.from(this.problems.values()).some(
            products => (products?.length ?? 0) > 0
        );
        this.hasProblems$.next(hasAny);
    }

    /** Returns only problem types that currently have at least one product */
    public getProblemTypesWithData(): PlanogramProblemType[] {
        return Array.from(this.problems.keys()).filter(
            type => (this.problems.get(type)?.length ?? 0) > 0
        );
    }
}